import type { AmbientParticlesConfig } from './AmbientParticlesConfig'

export const BOGGLE_AMBIENT_DEV_STORAGE_KEY = 'boggle.ambient-particles.dev.v1'

export function readAmbientDevConfig(): Partial<AmbientParticlesConfig> {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return {}
  }

  try {
    const raw = window.localStorage.getItem(BOGGLE_AMBIENT_DEV_STORAGE_KEY)
    if (!raw) {
      return {}
    }

    return sanitizeAmbientDevConfig(JSON.parse(raw))
  } catch {
    return {}
  }
}

export function sanitizeAmbientDevConfig(
  value: unknown,
): Partial<AmbientParticlesConfig> {
  if (!value || typeof value !== 'object') {
    return {}
  }

  const input = value as Partial<AmbientParticlesConfig> & { baseSpeed?: unknown }
  const output: Partial<AmbientParticlesConfig> = {}
  for (const key of [
    'alphaMax',
    'alphaMin',
    'baseHue',
    'driftX',
    'driftY',
    'fadeDurationMaxMs',
    'fadeDurationMinMs',
    'fullAlphaDurationMaxMs',
    'fullAlphaDurationMinMs',
    'hueJitterMaxDeg',
    'hueJitterMinDeg',
    'hueShift',
    'lightnessMax',
    'lightnessMin',
    'opacity',
    'particleCount',
    'saturationMax',
    'saturationMin',
    'sizeMax',
    'sizeMin',
    'spawnPaddingRatio',
    'speedMax',
    'speedMin',
  ] as const) {
    if (typeof input[key] === 'number' && Number.isFinite(input[key])) {
      output[key] = input[key]
    }
  }

  if (
    output.speedMin === undefined &&
    output.speedMax === undefined &&
    typeof input.baseSpeed === 'number' &&
    Number.isFinite(input.baseSpeed)
  ) {
    output.speedMin = Math.max(0, input.baseSpeed * 0.45)
    output.speedMax = Math.max(0, input.baseSpeed * 1.35)
  }

  if (
    input.maxFps === 'uncapped' ||
    (typeof input.maxFps === 'number' && input.maxFps > 0)
  ) {
    output.maxFps = input.maxFps === 30 ? 60 : input.maxFps
  }

  return output
}

export function persistAmbientDevConfig(dev: Partial<AmbientParticlesConfig>) {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(BOGGLE_AMBIENT_DEV_STORAGE_KEY, JSON.stringify(dev))
}
